import { jsx } from 'react/jsx-runtime';
import equal from 'fast-deep-equal';
import debounce from 'lodash.debounce';
import { useContext, createContext, useState, useEffect, useMemo } from 'react';
import { defaultResourceListPreferences, defaultSavePreferences } from './defaultPreferences.js';
import { useQueryUserInfo } from '../../../../user/queries/useQueryUserInfo.js';
import { getUserListViewFilters } from '../../../utils/getUserListViewPreferences.js';
import { captureException } from '@fabric-app/compat/capabilities/sentry';
import { useResponsive } from '@fabric-app/ui/hooks/useResponsive';

const ResourceListPreferencesContext = /* @__PURE__ */ createContext(void 0);
const resetColumnOrderNumbering = (columns) => {
  const sortedColumns = columns.sort((a, b) => a.order - b.order);
  return sortedColumns.map((col, index) => ({
    ...col,
    order: index
  }));
};
const defaultOrder = {
  property: "modifiedAt",
  direction: "DESC"
};
const DEFAULT_VIEW_MODE = "Grid";
const DEFAULT_AVAILABLE_VIEW_MODES = [
  "Grid",
  "List",
  "Masonry"
];
const ResourceListContextPreferencesProvider = ({ preferences: preferencesProp, savePreferences = defaultSavePreferences, disabledSortFields, disableSort, defaultViewMode = DEFAULT_VIEW_MODE, availableViewModes = DEFAULT_AVAILABLE_VIEW_MODES, children }) => {
  const { data: user } = useQueryUserInfo();
  const { isMobileView } = useResponsive();
  const [preferences, setPreferences] = useState(() => {
    if (preferencesProp && preferencesProp.version === 2) {
      return {
        ...defaultResourceListPreferences,
        ...preferencesProp
      };
    }
    return defaultResourceListPreferences;
  });
  useEffect(() => {
    setPreferences((prev) => {
      const next = {
        ...defaultResourceListPreferences,
        ...preferencesProp
      };
      if (preferencesProp && preferencesProp.version === 2 && !equal(prev, next)) {
        return next;
      }
      return prev;
    });
  }, [
    preferencesProp
  ]);
  const { setSort, setTableConfig, placeColumnAfter, setFilterKind, setFilterTagIds, setFilterUserIds, setViewMode } = useMemo(() => {
    const debouncedSave = debounce(savePreferences, 500);
    return {
      setFilterKind: (kind) => {
        setPreferences((prev) => {
          const nextState = {
            ...prev,
            filters: {
              ...prev.filters,
              kind
            }
          };
          debouncedSave(nextState);
          return nextState;
        });
      },
      setFilterTagIds: (tagIds) => {
        setPreferences((prev) => {
          const filterKey = user?.id ? `tagIds::${user?.id}` : "tagIds";
          const nextState = {
            ...prev,
            filters: {
              ...prev.filters,
              [filterKey]: tagIds
            }
          };
          debouncedSave(nextState);
          return nextState;
        });
      },
      setFilterUserIds: (userIds) => {
        setPreferences((prev) => {
          const nextState = {
            ...prev,
            filters: {
              ...prev.filters,
              userIds
            }
          };
          debouncedSave(nextState);
          return nextState;
        });
      },
      setSort: (sortField, options) => {
        setPreferences((prev) => {
          const nextState = {
            ...prev,
            filters: {
              ...prev.filters,
              order: {
                property: sortField,
                direction: "order" in options ? options.order : prev.filters.order?.direction === "ASC" ? "DESC" : "ASC"
              }
            }
          };
          debouncedSave(nextState);
          return nextState;
        });
      },
      setTableConfig: (arg) => {
        setPreferences((prev) => {
          const nextTableColumns = typeof arg === "function" ? arg(prev.tableColumns) : arg;
          const uniqueTableColumns = nextTableColumns.filter((column, index, self) => self.findIndex((c) => c.id === column.id) === index);
          const nextState = {
            ...prev,
            tableColumns: uniqueTableColumns
          };
          debouncedSave(nextState);
          return nextState;
        });
      },
      placeColumnAfter: (columnId, afterColumnId) => {
        setPreferences((prev) => {
          const columnToBePlacedAfter = prev.tableColumns.find((col) => col.id === afterColumnId);
          if (!columnToBePlacedAfter) {
            return prev;
          }
          const newOrderedColumns = resetColumnOrderNumbering(prev.tableColumns.map((item) => {
            if (item.id === columnId) {
              return {
                ...item,
                /**
                * we know the order is a round number (integer)
                * so we place this column after column X by setting order to be 0.5 higher
                *
                * then we reset the order numbers with resetColumnOrderNumbering
                */
                order: columnToBePlacedAfter.order + 0.5
              };
            }
            return item;
          }));
          const nextState = {
            ...prev,
            tableColumns: newOrderedColumns
          };
          debouncedSave(nextState);
          return nextState;
        });
      },
      setViewMode: (viewMode2) => {
        setPreferences((prev) => {
          const nextState = {
            ...prev,
            viewMode: viewMode2
          };
          savePreferences(nextState);
          return nextState;
        });
      }
    };
  }, [
    user?.id,
    savePreferences
  ]);
  const { filters, filtersAppliedCount } = useMemo(() => {
    const filters2 = user?.id ? getUserListViewFilters(preferences.filters, user.id) : preferences.filters;
    const filtersAppliedCount2 = Object.entries(filters2).filter(([key, value]) => {
      if (key === "order") {
        return false;
      }
      if (Array.isArray(value) && value.length === 0) {
        return false;
      }
      if (value) {
        return true;
      }
      return false;
    }).length;
    return {
      filters: filters2,
      filtersAppliedCount: filtersAppliedCount2
    };
  }, [
    preferences.filters,
    user?.id
  ]);
  const viewMode = useMemo(() => (
    /**
    * if there's a viewmode disabled, we override it with grid view
    * if mobile view, we always show grid view
    */
    isMobileView || !availableViewModes.includes(preferences.viewMode) ? defaultViewMode : preferences.viewMode || "Grid"
  ), [
    isMobileView,
    availableViewModes,
    defaultViewMode,
    preferences.viewMode
  ]);
  const contextValue = {
    tableColumns: preferences.tableColumns,
    setTableConfig,
    placeColumnAfter,
    setSort,
    setFilterKind,
    setFilterTagIds,
    setFilterUserIds,
    sort: preferences.filters.order || defaultOrder,
    disabledSortFields,
    disableSort,
    filtersAppliedCount,
    filters,
    defaultViewMode,
    availableViewModes,
    viewMode,
    setViewMode
  };
  return /* @__PURE__ */ jsx(ResourceListPreferencesContext.Provider, {
    value: contextValue,
    children: typeof children === "function" ? children(contextValue) : children
  });
};
const useResourceListPreferencesContext = () => {
  const context = useContext(ResourceListPreferencesContext);
  if (!context) {
    captureException("useResourceListPreferencesContext: context not found");
    throw new Error("useResourceListPreferencesContext: context not found");
  }
  return context;
};
const useResourceListPreferencesContextUnsafe = () => {
  const context = useContext(ResourceListPreferencesContext);
  return context;
};

export { ResourceListContextPreferencesProvider, useResourceListPreferencesContext, useResourceListPreferencesContextUnsafe };
//# sourceMappingURL=resourceListContextPreferences.js.map
